import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import { useSurveyParams } from '../context/SurveyParamsContext'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertTriggers,
  convertQuestionMappings,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
    // useUserIdLogin: true,
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = 'all'
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 名單式調查參數
  const { sfdSamplerId, sfdPId } = useSurveyParams()

  // 防止閃閃
  useEffect(() => {
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
              console.log('✅ 強制加上 mobile class 避免抖動')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })

      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 以下皆為 1-based 寫法，convertVisibleIf 會自動 -1 還原為 0-based

  // A1(q13) 婚姻狀況：1未婚 2已婚 3未婚同居 4分居 5離婚 6喪偶
  // 有配偶或同居伴侶（A6、B7、D7、D9 限定查填）
  const hasPartner =
    '({q13} = 1 and ({q14} = 2 or {q14} = 3)) or ' +
    '({q13} = 2) or ' +
    '({q13} = 3) or ' +
    '({q13} = 4) or ' +
    '({q13} = 5 and ({q18} = 2 or {q18} = 3)) or ' +
    '({q13} = 6 and ({q19} = 2 or {q19} = 3))'

  // A4-1/A4-2/A4-3 限「有子女且子女年齡含 0~未滿6歲」者（A4 註記：2-1至2-3皆未勾選跳答A5）
  const hasChildUnder6 = '({q22} = 2) and ({q25} anyof [1, 2, 3])'

  // B1(q51)=1 有工作 → 續答 B2~B6-1；(2-1)或答完(2-2)一律跳答 B7
  const isEmployed = '{q51} = 1'

  const visibleIfConditionsInit = convertVisibleIf({
    // ===== 基本資料 =====
    // Q2-1(q4)=(1)無/不識字 → 跳答 Q3-1，即跳過 Q2-2
    q5: '{q4} <> 1',
    // Q4-1(q9) 勾「(1)獨居」(none) → 跳答 Q4-3，即跳過 Q4-2
    q10: '{q9} notcontains ["none"]',

    // ===== A 部分：婚育狀況 =====
    // A1 六個分支子題
    q14: '{q13} = 1',
    q15: '{q13} = 2',
    q16: '{q13} = 3',
    q17: '{q13} = 4',
    q18: '{q13} = 5',
    q19: '{q13} = 6',
    // A2 未婚原因：已婚/分居跳答 A4、離婚/喪偶跳答 A3，皆不問
    q20: '{q13} = 1 or {q13} = 3',
    // A3 結婚再婚意願：限未婚、未婚同居、離婚、喪偶（題幹自述限制）
    q21: '{q13} = 1 or {q13} = 3 or {q13} = 5 or {q13} = 6',
    // A4 子女人數與年齡
    q23: '{q22} = 2',
    q24: '{q22} = 2',
    q25: '{q22} = 2',
    q26: '({q22} = 2) and ({q25} anyof [1])',
    q27: '({q22} = 2) and ({q25} anyof [2])',
    q28: '({q22} = 2) and ({q25} anyof [3])',
    q29: '({q22} = 2) and ({q25} anyof [4])',
    q30: '({q22} = 2) and ({q25} anyof [5])',
    q31: '({q22} = 2) and ({q25} anyof [6])',
    // A4-1 托育花費 / A4-2 照顧方式 / A4-3 育嬰留停
    q32: hasChildUnder6,
    q33: hasChildUnder6,
    q34: '(' + hasChildUnder6 + ') and ({q33} anyof [18])',
    q35: hasChildUnder6,
    // A5 理想子女數
    q37: '{q36} = 1',
    q38: '({q36} = 1) and ({q37} = 1)',
    q39: '({q36} = 1) and ({q37} = 1)',
    q40: '({q36} = 1) and ({q37} = 2)',
    q41: '{q36} = 2',
    q42: '{q36} = 2',
    // A6 配偶／伴侶的理想子女數【限有配偶(同居伴侶)者查填】
    q43: hasPartner,
    q44: '(' + hasPartner + ') and ({q43} = 1)',
    q45: '(' + hasPartner + ') and ({q43} = 1) and ({q44} = 1)',
    q46: '(' + hasPartner + ') and ({q43} = 1) and ({q44} = 1)',
    q47: '(' + hasPartner + ') and ({q43} = 1) and ({q44} = 2)',
    q48: '(' + hasPartner + ') and ({q43} = 2)',
    q49: '(' + hasPartner + ') and ({q43} = 2)',

    // ===== B 部分：就業與工作 =====
    // B1(q51)=2 沒有 → B1-2；(2-2)沒找工作 → 原因與最主要一項
    q52: '{q51} = 2',
    q53: '({q51} = 2) and ({q52} = 2)',
    q54: '({q51} = 2) and ({q52} = 2)',
    // B2~B6-1 限有工作者
    q55: isEmployed,
    q56: isEmployed,
    q57: isEmployed,
    q58: isEmployed,
    q59: isEmployed,
    q60: isEmployed,
    q61: isEmployed,
    q62: isEmployed,
    // B7 配偶有無工作【限有配偶(同居伴侶)者查填】
    q63: hasPartner,
    // B9 六列：(2)沒有 → 跳下一列；A欄選「未曾恢復工作」→ 跳下一列
    // A欄「在離職__年__月後曾恢復工作」為 other 選項
    q66: '{q65} = 1',
    q67: '({q65} = 1) and ({q66} = "other")',
    q68: '({q65} = 1) and ({q66} = "other")',
    q70: '{q69} = 1',
    q71: '({q69} = 1) and ({q70} = "other")',
    q72: '({q69} = 1) and ({q70} = "other")',
    q74: '{q73} = 1',
    q75: '({q73} = 1) and ({q74} = "other")',
    q76: '({q73} = 1) and ({q74} = "other")',
    q78: '{q77} = 1',
    q79: '({q77} = 1) and ({q78} = "other")',
    q80: '({q77} = 1) and ({q78} = "other")',
    q82: '{q81} = 1',
    // 注意：B9-5 的 q83 是恢復類型、q84 是恢復原因（JSON 內順序與其他列相反）
    q83: '({q81} = 1) and ({q82} = "other")',
    q84: '({q81} = 1) and ({q82} = "other")',
    q86: '{q85} = 1',
    q87: '({q85} = 1) and ({q86} = "other")',
    q88: '({q85} = 1) and ({q86} = "other")',

    // ===== C 部分：經濟安全 =====
    // C1(q89) 選(1)沒有收入 → 跳轉至 C3(q93)，即跳過 C1-2、C2、C2-2
    q90: '{q89} = 2',
    q91: '{q89} <> 1',
    q92: '({q89} <> 1) and ({q91} = 2)',
    q94: '{q93} = 2',
    q96: '{q95} anyof [12]',
    q98: '{q97} = 2',

    // ===== D 部分：家庭照顧 =====
    // D3 各列：A欄「有」為 other 選項；B欄選(2)我是主要照顧者才問輪替照顧者
    q102: '{q101} = "other"',
    q103: '({q101} = "other") and ({q102} = 2)',
    q104: '({q101} = "other") and ({q102} = 2) and ({q103} = 1)',
    q106: '{q105} = "other"',
    q107: '({q105} = "other") and ({q106} = 2)',
    q108: '({q105} = "other") and ({q106} = 2) and ({q107} = 1)',
    q110: '{q109} = "other"',
    q111: '({q109} = "other") and ({q110} = 2)',
    q112: '({q109} = "other") and ({q110} = 2) and ({q111} = 1)',
    q114: '{q113} = "other"',
    q115: '({q113} = "other") and ({q114} = 2)',
    q116: '({q113} = "other") and ({q114} = 2) and ({q115} = 1)',
    // D7 配偶無酬照顧時間【限有配偶或同居伴侶者】
    q122: hasPartner,
    q123: hasPartner,
    q124: hasPartner,
    q125: hasPartner,
    q126: hasPartner,
    q127: hasPartner,
    // D9 提升配偶參與家務意願【限有配偶(同居伴侶)者查填】
    q129: hasPartner,
    q130: '(' + hasPartner + ') and ({q129} anyof [7])',

    // ===== E 部分：社會參與 =====
    // E1(q131) 勾「(8)都沒有」(none) 才續問 E2，其餘跳答 F1
    q132: '{q131} anyof ["none"]',

    // ===== F 部分：醫療健康與求助 =====
    q136: '{q135} = 2',
    q137: '({q135} = 2) and ({q136} anyof [9])',
    // F7(q141) 選「(1)沒有達到規律運動」(other) 才問 F8，選「(2)有」跳答 G1
    q142: '{q141} = "other"',

    // ===== H 部分：其他（排序題的「其他，請說明」）=====
    q148: '{q147} anyof [16]',
    q150: '{q149} anyof [11]',
    q152: '{q151} anyof [9]',
  })

  const notRequired = null
  const mappedRateChoice = null
  const pageBreakQuestions = null

  // 同意書(q1)：選(2)本人不同意參與研究 → 直接結束問卷
  const triggersConditionsInit = convertTriggers([
    {
      type: 'complete',
      expression: `({q1} = 2)`,
    },
  ])
  // 選項繼承：複選的原因題 → 「最主要一項」單選題，只帶出前題勾選的選項
  const questionMappings = convertQuestionMappings({
    q41: 'q42', // A5-2 不想有小孩原因 → A5-2-a 最主要一項
    q48: 'q49', // A6-2 配偶不想有小孩原因 → A6-2-a 最主要一項
    q53: 'q54', // B1-2-2 沒找工作原因 → B1-2-2-a 最主要一項
  })

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
    sfdSamplerId,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  // 結尾語：本案客製（不使用 EZChoice 預設結語）
  // 完成與同意書(q1)選(2)不同意中止，兩條路徑都顯示同一段結尾語
  const endSurveyMsg = (survey) => {
    setCompletionMessage(
      `<div>謝謝您的協助，如有任何問題請致電02-23315133#601~604典通股份有限公司，或致電1999（外縣市02-27208889）#6969社會局婦女福利及兒童托育科詢問。</div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving:
      requiresLogin.login || webAuthType > 0 || !!sfdSamplerId,
    webAuthType,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
